<script lang='ts'>
import { onMount } from "svelte";
import Config from "../config.json";
import { BaseUrl, getJson, sendJson } from "../services";
import { groupBy } from "../util";
import Dots from "./Dots.svelte";
import LoginLayout from './LayoutLogin.svelte';

const PaymentSandbox = {
  authData: {
    clientKey: "77jnrX8NyBm3q4r7meT5uA7F2FR834aHUFTX9W6M2Q34umu9cMMd2fey7954K8FS",
    apiLoginID: "9a2mwGA7A"
  },
  live: false
}

const PaymentLive = {
  authData: {
    clientKey: "5hmHkdCANWEWpx26XRAtSsv2RHfhuN3gb6C9KYUp9c4uB4EyHQ5m8f2U6XcE4f4W",
    apiLoginID: "85XZaK9bR"
  },
  live: true
}

const PaymentConfig = PaymentLive;
(<any>window).paymentconfig = PaymentConfig;

type TicketTypes = {
  id: string;
  name: string;
  available: boolean;
  price: number;
  discount?: {
    break: number;
    price: number;
  }
}[];

interface TicketStateResponse {
  types: TicketTypes;
  session: string;
}

interface User {
  email: string;
  first: string;
  last: string;
  type: string; // ticket type
  error?: string;
}

interface Receipt {
  purchaseNo: string;
  tickets: {
    code: string;
    email: string;
  }[];
}

enum Page {
  Start,
  Guests,
  Pay,
  Receipt
}

export let header = '';
export let video = '';
export let sponsors = '';
export let content = '';

const Today = new Date();
const MinCardYear = Today.getFullYear();

let session;
let ticketTypes: TicketTypes;
let loading = false;
let soldOut = false;

let receipt: Receipt;
let minCardMonth = -1;

let page: Page = Page.Start;
let users: User[] = [];

let cardNumber = '';
let month = Today.getMonth() + 1;
let year = MinCardYear;
let cardCode = '';
let zip = '';
let name = '';
let notifyEmail = '';
let purchasing = false;
let checkingUsers = false;
let ticketOrder;

let checkoutError;

function addGuest() {
  users = [...users, {email: '', first: '', last: '', type: undefined}]
}

function removeGuest(user) {
  users = users.filter(u => u !== user);
}

function handleSoldOutError(e) {
  const ids = e.data.ids;
  
  ticketTypes = ticketTypes.filter(t => !ids.includes(t.id));

  checkoutError = e.message;
  users = users.map(u => ({
    ...u,
    type: ids.includes(u.type) ? undefined : u.type,
    error: 'Selected ticket for this user was sold out'
  }));
  page = Page.Guests;
}

async function continueFromInfo() {
  if (checkingUsers) return;
  checkingUsers = true;
  checkoutError = undefined;

  try {
    // trim all fields
    users = users.map(u => ({
      email: u.email.trim(),
      first: u.first.trim(),
      last: u.last.trim(),
      type: u.type
    }));

    // Collect errors
    const emails = {}
    users.forEach((user, i) => {
      if (user.email.length === 0 || user.first.length === 0 || user.last.length === 0 || !user.type) {
        user.error = 'Please provide all fields';
        return;
      }

      const emailParts = user.email.split('@');
      if (emailParts.length !== 2) {
        user.error = 'Please provide a valid email address';
        return;
      }

      if (emails[user.email]) {
        user.error = 'This email was already provided';
        return;
      }

      emails[user.email] = true;
            
      user.error = undefined;
    });

    let errorCount = users.reduce((sum, user) => sum + user.error ? 1 : 0, 0);
    if (errorCount === 0) {
      try {
        await sendJson<any>(`${BaseUrl}/api/tickets/verify`, {
          eventId: Config.eventId,
          companyId: Config.companyId,
          users
        });
      } catch (e) {
        console.log(e)
        if (e.code === 'SoldOut') return handleSoldOutError(e);

        if (!e?.data?.emails) throw e;

        Object.entries(e.data.emails).forEach(([email, response]) => {
          const user = users.find(u => u.email === email);
          console.log(email, response, user)
          if (response === 'ticketed') user.error = 'This user already has a ticket for this event';
          else if (response === 'notallowed') user.error = `
    <p class='mb-3'>We're sorry but this guest is not able to be registered at this time.  Listed below are possible explanations for the failure:</p>

    <ul class="list-disc mb-3">
    <li>You are not a VelocityEHS customer</li>
    <li>You entered a personal email address</li>
    <li>You misspelled your email address</li>
    </ul>

    <p class='mb-3'>Please check that you are using your corporate email address.</p>
    <p class='mb-3'>If you experience additional technical difficulties, email <a href='mailto:events@ehs.com' target="_blank" style="color: #3f37c9">events@ehs.com</a>.</p>
            `;
        });
      }
    }
    
    users = users;

    // Advance if no errors
    errorCount = users.reduce((sum, user) => sum + user.error ? 1 : 0, 0);
    if (errorCount === 0) {
      const computeTicketPrice = (ticket, quantity) => {
        const discount = ticket.discount && quantity >= ticket.discount.break;
        const price = discount ? ticket.discount.price : ticket.price;
        return { discount, price }
      }

      const groups = groupBy(users, 'type');
      ticketOrder = {
        price: Object.values(groups).reduce((total, users) => {
          const ticket = ticketTypes.find(t => t.id === users[0].type);
          return total + computeTicketPrice(ticket, users.length).price * users.length;
        }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
        groups: Object.values(groups).map(users => {
          const ticket = ticketTypes.find(t => t.id === users[0].type);
          const tprice = computeTicketPrice(ticket, users.length);
          return {
            quantity: users.length,
            name: ticket.name,
            discount: tprice.discount,
            total: (tprice.price * users.length).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
            price: tprice.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          }
        })
      }

      page = Page.Pay;
    }
  } catch (e) {
    checkoutError = e.message;
  } finally {
    checkingUsers = false;
  }
}

function reset() {
  checkoutError = undefined;
  page = Page.Start;
  users = []
  addGuest();
}

async function submitPayment() {
  if (purchasing) return;

  purchasing = true;
  checkoutError = undefined;

  try {
    const secureData = {
      authData: PaymentConfig.authData,
      cardData: {
        cardNumber,
        month: month.toString(),
        year: year.toString(),
        cardCode,
        zip
      }
    };

    const response = await new Promise((resolve, reject) => {
      // @ts-ignore
      Accept.dispatchData(secureData, response => {
        console.log("response", response)
        if (response.messages.resultCode === "Error") {
          const first = response.messages.message[0];
          for (const msg of response.messages.message) {
            console.log(msg.code, msg.text);
          }
          reject(first ? {
            code: first.code,
            message: first.text
          } : {
            code: 0,
            message: 'Authorization issue. Please try again.'
          });
        } else {
          resolve(response.opaqueData);
        }
      });
    });

    receipt = await sendJson<any>(`${BaseUrl}/api/tickets/pay`, {
      session,
      nonce: response,
      eventId: Config.eventId,
      companyId: Config.companyId,
      email: notifyEmail,
      
      firstname: name,
      lastname: '',
      address: '',
      city: '',
      state: '',
      zip,
      users
    });
    
    page = Page.Receipt;
  } catch (e) {
    console.error(e);
    if (e.code === 'SoldOut') return handleSoldOutError(e);

    checkoutError = e.message;
  } finally {
    purchasing = false;
  }
}

async function load() {
  loading = true;
  try {
    ticketTypes = undefined;
    const result: TicketStateResponse = await getJson(`${BaseUrl}/api/tickets/${Config.eventId}`);
    ({types: ticketTypes, session} = result);
    addGuest();

    soldOut = ticketTypes.every(t => !t.available);
  } finally {
    loading = false;
  }
}

onMount(() => {
  load()
});

$: minCardMonth = year == MinCardYear ? Today.getMonth() + 1 : -1;
$: cardNumber = cardNumber.replace(/[^0-9]/g, '');
$: cardCode = cardCode.replace(/[^0-9]/g, '');
$: users = users.map(u => ({
  ...u,
  email: u.email.toLowerCase()
}));
</script>

<svelte:head>
  {#if PaymentConfig.live}
    <script type="text/javascript" src="https://js.authorize.net/v1/Accept.js" charset="utf-8"> </script>
  {:else}
    <script type="text/javascript" src="https://jstest.authorize.net/v1/Accept.js" charset="utf-8" ></script>
  {/if}
</svelte:head>

<LoginLayout title={Config.title} privacyUrl={Config.privacyUrl} termsUrl={Config.termsUrl} logo={Config.logo} background={Config.login.background}>
  <div class='flex-col-reverse flex w-full max-w-screen-xl gap-4 px-2 md:flex-row'>

    <!-- Video & sponsors -->
    <div class='flex flex-col gap-4 w-full md:w-80'>
      <iframe
        title="Welcome"
        src={video}
        frameborder="0"
        scrolling="no"
        allow="autoplay"
        class="boxstyle w-full h-48 bg-[#2C3235]" />
      <div class='flex flex-col boxstyle bg-white'>
        {@html sponsors}
      </div>
    </div>
    <div class='flex flex-col items-center flex-1 boxstyle p-4 bg-white'>
      <div class='flex flex-col'>
        <div class='mb-6 text-center'>
          {@html header}
        </div>
        {#if page === Page.Start}
          {@html content}

          <div class='flex flex-col items-center gap-2'>
            {#if loading}
              <div class='spinner' />
            {:else if soldOut}
              <div class='btn cursor-auto'>SOLD OUT!</div>
            {:else}
              <div class='btn' on:click={() => page++}>BUY TICKETS NOW</div>
            {/if}
            <div>Already registered? <a style='color: #3f37c9' href='/signin'>Login</a></div>
            <div class='flex gap-1'>
              <Dots --color='var(--secondary)' total={4} page={0} />
            </div>
          </div>
        {:else if page === Page.Guests}
          <div class='flex flex-col items-center gap-2 max-w-xl'>
            <div class='flex flex-col self-stretch'>
              {#each users as user, n}
                <div class='flex items-center h-10'>
                  <h3 class="text-black font-bold text-2xl mr-2">Guest {n+1}</h3>
                  {#if users.length > 1}
                    <div class='flex items-center cursor-pointer select-none p-1' on:click={() => removeGuest(user)}>
                      <span class='material-icons' style='color: var(--secondary)'>remove_circle</span>
                      <span class=''>Remove</span>
                    </div>
                  {/if}
                </div>
                {#if user.error}
                  <div class='text-red-500 text-sm'>{@html user.error}</div>
                {/if}
                <div class="flex flex-col gap-2 mb-6">
                  <div class="flex flex-col gap-2 md:flex-row">
                    <div class='flex-1'>
                      <label for="first-name" class="block text-sm font-medium text-gray-700">First name</label>
                      <div class="mt-1">
                        <input bind:value={user.first} type="text" name="first-name" id="first-name" autocomplete="given-name" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                      </div>
                    </div>

                    <div class='flex-1'>
                      <label for="last-name" class="block text-sm font-medium text-gray-700">Last name</label>
                      <div class="mt-1">
                        <input bind:value={user.last} type="text" name="last-name" id="last-name" autocomplete="family-name" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-col gap-2 md:flex-row">
                    <div class='flex-1'>
                      <label for="email" class="block text-sm font-medium text-gray-700">Email address</label>
                      <div class="mt-1">
                        <input bind:value={user.email} id="email" name="email" type="email" autocomplete="email" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                      </div>
                    </div>

                    <div class='flex-1'>
                      <label for="ticket-type" class="block text-sm font-medium text-gray-700">Ticket type</label>
                      <div class="mt-1">
                        <select bind:value={user.type} id='ticket-type' class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option hidden disabled selected value></option>
                          {#each ticketTypes as type (type.id)}
                            <option value={type.id} disabled={!type.available}>{type.name} - {type.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} {!type.available ? ' - SOLD OUT' : ''}</option>
                          {/each}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              {/each}
            </div>
            
            <div class='flex items-center cursor-pointer p-2 mb-4' on:click={addGuest}>
              <span class='material-icons' style='color: var(--secondary)'>add_circle</span>
              <span class='text-lg font-medium select-none'>Add Guest</span>
            </div>

            {#if checkoutError}
              <div class='text-red-500 text-sm my-6 text-center'>{checkoutError}</div>
            {/if}

            <div class='flex gap-2'>
              <div class='btn primary' on:click={reset}>BACK</div>
              <div class='btn' on:click={continueFromInfo}>
                {#if checkingUsers}
                  <div class='spinner' />
                {:else}
                  CONTINUE
                {/if}
              </div>
            </div>
            <div class='flex gap-1'>
              <Dots --color='var(--secondary)' total={4} page={1} />
            </div>
          </div>
          
        {:else if page === Page.Pay}

          <div class='text-center mb-6'>
            {#each Object.values(ticketOrder.groups) as group}
              <div>x{group.quantity} - {group.name} - {group.total} ({group.price}/each{@html group.discount ? ' <b>DISCOUNT PRICE!</b>' : ''})</div>
            {/each}
            <div>Total: {ticketOrder.price}</div>
          </div>

          <div class='flex flex-col gap-2'>
            <div>
              <label for="name-on-card" class="block text-sm font-medium text-gray-700">Name on card</label>
              <div class="mt-1">
                <input bind:value={name} type="text" id="name-on-card" name="name-on-card" autocomplete="cc-name" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
            </div>

            <div>
              <label for="card-number" class="block text-sm font-medium text-gray-700">Card number</label>
              <div class="mt-1">
                <input bind:value={cardNumber} type="text" id="card-number" name="card-number" autocomplete="cc-number" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
            </div>

            <div class='flex gap-2 flex-wrap'>
              <div class='flex flex-col flex-shrink-0'>
                <label for="expiration-date" class="block text-sm font-medium text-gray-700">Expiration date (MM / YY)</label>
                <div class='flex gap-2 mt-1'>
                  <div>
                    <div>
                      <select class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" id="cardMonth" bind:value={month} data-ref="cardDate">
                        <option value="" disabled selected>Month</option>
                        {#each Array(12) as _, n}
                          {#if n + 1 >= minCardMonth}
                            <option value={(n+1) < 10 ? '0' + (n+1) : (n+1)}>
                                {(n+1) < 10 ? '0' + (n+1) : (n+1)}
                            </option>
                          {/if}
                        {/each}
                      </select>
                    </div>
                  </div>

                  <div>
                    <select class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" id="cardYear" bind:value={year} data-ref="cardDate">
                      <option value="" disabled selected>Year</option>
                      {#each Array(12) as _, n}
                        <option value={n + MinCardYear}>
                            {n + MinCardYear}
                        </option>
                      {/each}
                    </select>
                  </div>
                </div>
              </div>

              <div class='flex-1'>
                <label for="cvc" class="block text-sm font-medium text-gray-700">CVC</label>
                <div class="mt-1">
                  <input bind:value={cardCode} type="text" name="cvc" id="cvc" autocomplete="csc" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                </div>
              </div>

              <div class='w-full h-0 md:hidden' />

              <div class='flex-1'>
                <label for="zip" class="block text-sm font-medium text-gray-700">Zip</label>
                <div class="mt-1">
                  <input bind:value={zip} type="text" name="zip" id="zip" autocomplete="zip" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                </div>
              </div>
            </div>

            <div>
              <label for="email-address" class="block text-sm font-medium text-gray-700">Send receipt this email address</label>
              <div class="mt-1">
                <input bind:value={notifyEmail} type="email" id="email-address" name="email-address" autocomplete="email" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
            </div>
          </div>

          {#if checkoutError}
            <div class='text-red-500 text-sm my-6 text-center'>{checkoutError}</div>
          {/if}

          <div class='flex flex-col items-center gap-2 mt-12'>
            <div class='flex gap-2'>
              <div class='btn primary' on:click={() => page = Page.Guests}>BACK</div>
              <button type="submit" class='btn mx-auto' on:click={submitPayment}>
                {#if purchasing}
                  <div class='spinner' />
                {:else}
                  COMPLETE REGISTRATION
                {/if}
              </button>
            </div>
            <div class='flex gap-1'>
              <Dots --color='var(--secondary)' total={4} page={2} />
            </div>
          </div>
        {:else if page === Page.Receipt}
          <div class='flex flex-col gap-2 text-center items-center mt-8'>
            <div class='text-center font-bold text-[32px]'>You're all set! Thank you for registering!</div>

            <div>A confirmation email will land in all guests' inboxes shortly.</div>
            <div>Confirmation #: {receipt?.purchaseNo}</div>
            <div>Registered Guests:</div>
            <div class='ml-5'>
              {#each receipt?.tickets || [] as ticket}
                <div>{ticket.email} - Ticket Code: {ticket.code}</div>
              {/each}
            </div>
            <div>We look forward to seeing you there!</div>

            <div class='flex gap-1 mt-4'>
              <Dots --color='var(--secondary)' total={4} page={3} />
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</LoginLayout>

<style>
.boxstyle {
  @apply border-[#2C3235] md:border-8;
}
</style>